import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Avatar from '../../../components/ui/Avatar';
import Button from '../../../components/ui/Button';
import GenericSelect from '../../../components/ui/GenericSelect';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import PanelCard from '../../performance-operations/components/PanelCard';
import { Badge, Grid, KpiSub, KpiValue, Table } from '../../performance-operations/styles';
import { formatNumber } from '../../performance-operations/config';
import { formatDate } from '../../../utils/Utils';
import { SUMMARY_FIELDS } from '../config';

// P7a — E14 üst alan (çalışan, dönem organizasyonu, yönetici, sonuç durumu; M52/M53/M54), LLM yönetici özeti paneli ve sağ çekmece
// (hesaplama dökümü, kanıt kaydı, hedef ayrıntısı).

const HeaderCard = styled.section`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  margin-bottom: 16px;
  background: ${({ theme }) => theme.colors.surface.card};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: 10px;
`;

const Who = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 200px;
  flex: 1;
`;

const Name = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Meta = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Field = styled.div`
  width: 240px;
  max-width: 100%;
  @media (max-width: 768px) { width: 100%; }
`;


const Text = styled.p`
  margin: 0 0 10px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.6;
`;

const SourceChip = styled.span`
  display: inline-block;
  margin: 0 4px 4px 0;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  background: ${({ theme }) => theme.colors.bgColor};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Label = styled.h4`
  margin: 12px 0 6px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Muted = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const num = (v, lang, digits = 2) => (v == null ? '-' : formatNumber(v, lang, digits));

export function ProfileHeader({ profile, onPeriodChange, onOpenBreakdown }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const e = profile.employee;
  const h = profile.header;
  const [first, ...rest] = (e.name || '').split(' ');
  const finalText = h?.finalLabel || (h?.finalValue != null ? `${num(h.finalValue, lang)}${h.scaleMax ? ` / ${num(h.scaleMax, lang, 0)}` : ''}` : null);
  return (
    <>
      <HeaderCard data-section="profile-header">
        <Avatar src={e.profilePicture} firstName={first} lastName={rest.join(' ')} size={56} />
        <Who>
          <Name>{e.name}</Name>
          <Meta>{[e.titleName, e.departmentName].filter(Boolean).join(' · ') || '-'}</Meta>
          <Meta>{e.managerName ? t('performance.profile.manager', { name: e.managerName }) : t('performance.profile.noManager')}</Meta>
        </Who>
        {h ? (
          <div>
            <Badge $tone={h.published ? 'success' : 'info'} data-result-state={h.resultState}>
              {t(`performance.profile.state.${h.resultState}`, { defaultValue: h.resultState })}
            </Badge>
            {h.preliminary ? <Meta>{t('performance.profile.preliminaryHrOnly')}</Meta> : null}
          </div>
        ) : null}
        <Field>
          <GenericSelect
            label={t('performance.profile.period')}
            value={profile.periodId || ''}
            clearable={false}
            onChange={onPeriodChange}
            options={profile.periods.map((p) => ({ id: p.periodId, name: p.name }))}
          />
        </Field>
      </HeaderCard>
      {h ? (
        <Grid style={{ marginBottom: 16 }}>
          <PanelCard
            panelKey="kpi-M52"
            kpi
            compact
            span={3}
            title={t('performance.profile.kpi.M52')}
            actions={(
              <>
                <Badge $tone="info">M52</Badge>
                <Button variant="ghost" onClick={onOpenBreakdown} data-action="open-breakdown">{t('performance.profile.openBreakdown')}</Button>
              </>
            )}
          >
            <KpiValue data-kpi="M52">{finalText || t('performance.aiCenter.noData')}</KpiValue>
          </PanelCard>
          <PanelCard
            panelKey="kpi-M53"
            kpi
            compact
            span={3}
            title={t('performance.profile.kpi.M53')}
            actions={(
              <>
                <Badge $tone="info">M53</Badge>
                <Button variant="ghost" onClick={onOpenBreakdown}>{t('performance.profile.openBreakdown')}</Button>
              </>
            )}
          >
            <KpiValue data-kpi="M53">{h.goalScore != null ? `${num(h.goalScore, lang)} / 100` : t('performance.aiCenter.noData')}</KpiValue>
          </PanelCard>
          <PanelCard panelKey="kpi-M54" kpi compact span={3} title={t('performance.profile.kpi.M54')} actions={<Badge $tone="info">M54</Badge>}>
            <KpiValue data-kpi="M54">{h.competencyScore != null ? `${num(h.competencyScore, lang)} / 5` : t('performance.aiCenter.noData')}</KpiValue>
          </PanelCard>
          <PanelCard panelKey="kpi-band" kpi compact span={3} title={t('performance.profile.kpi.band')}>
            <KpiValue data-kpi="band">{h.band ? t(`performance.profile.band.${h.band}`) : t('performance.aiCenter.noData')}</KpiValue>
            {h.acknowledgedAt ? <KpiSub>{t('performance.profile.acknowledged', { date: formatDate(h.acknowledgedAt, lang) })}</KpiSub> : null}
          </PanelCard>
        </Grid>
      ) : null}
    </>
  );
}

/** LLM yönetici özeti: her ifade kaynak kodlarıyla; metindeki sayılar sunucuda kanıta karşı doğrulanmıştır. */
export function AiSummaryPanel({ available, summary, generating, onGenerate }) {
  const { t } = useTranslation();
  if (!available) return null;
  return (
    <PanelCard
      panelKey="profile-ai-summary"
      span={12}
      title={t('performance.profile.aiSummary')}
      actions={(
        <Button variant="outline" onClick={onGenerate} disabled={generating} loading={generating} data-action="generate-summary">
          {summary ? t('performance.profile.regenerateSummary') : t('performance.profile.generateSummary')}
        </Button>
      )}
      empty={!summary}
      emptyText={t('performance.profile.aiSummaryHint')}
    >
      {summary ? (
        <>
          {SUMMARY_FIELDS.filter((f) => summary.fields?.[f]).map((f) => (
            <div key={f} data-summary-field={f}>
              <Label>{t(`performance.profile.summaryField.${f}`)}</Label>
              {summary.statements.filter((s) => s.field === f).map((s) => (
                <Text key={s.ordinal} data-kind={s.kind}>
                  {s.text}{' '}
                  {s.sources.map((src) => (
                    <SourceChip key={src.ref} data-source={src.type}>
                      {t(`performance.aiCenter.sourceType.${src.type}`, { defaultValue: src.type })}{src.label ? `: ${src.label}` : ''}
                    </SourceChip>
                  ))}
                </Text>
              ))}
            </div>
          ))}
          <Muted>{t('performance.profile.aiSummaryVerified', { sourced: summary.sourcedCount, total: summary.verifiableCount })}</Muted>
        </>
      ) : null}
    </PanelCard>
  );
}

/** Sağ çekmece: hesaplama dökümü (M52/M53 eşleşmesi), kanıt kaydı ya da hedef ayrıntısı. Salt okunur. */
export function EvidenceDrawer({ open, item, profile, onClose }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  if (!item) return null;
  let title = '';
  let body = null;
  if (item.type === 'breakdown') {
    const b = profile.breakdown;
    title = t('performance.profile.breakdownTitle');
    body = !b ? <Muted>{t('performance.aiCenter.noData')}</Muted> : (
      <>
        <Table>
          <thead><tr><th>{t('performance.profile.goal')}</th><th>{t('performance.profile.score')}</th><th>{t('performance.profile.weight')}</th><th>{t('performance.profile.contribution')}</th></tr></thead>
          <tbody>
            {b.goals.map((g) => (
              <tr key={g.goalId}><td>{g.title}</td><td>{num(g.score, lang)}</td><td>%{num(g.effectiveWeight, lang)}</td><td>{num(g.contribution, lang)}</td></tr>
            ))}
            <tr data-row="total"><td>{t('performance.profile.goalTotal')}</td><td /><td /><td>{num(b.goalTotal, lang)}</td></tr>
          </tbody>
        </Table>
        <Muted>{t('performance.profile.projectedGoalScore', { value: num(b.projectedGoalScore, lang) })}</Muted>
        {b.matchesFinal != null ? (
          <Muted data-matches={String(b.matchesFinal)}>{b.matchesFinal ? t('performance.profile.matchesFinal') : t('performance.profile.notMatchesFinal')}</Muted>
        ) : null}
        {b.combinedEnabled ? (
          <>
            <Label>{t('performance.profile.combinedTitle')}</Label>
            <Muted>{t('performance.profile.combinedFormula', {
              goal: num(b.goalTotal, lang), goalWeight: b.goalWeight, feedback: num(b.feedbackScore, lang), feedbackWeight: b.feedbackWeight, total: num(b.combinedTotal, lang),
            })}</Muted>
          </>
        ) : null}
        {b.proposedLevelLabel || b.finalLevelLabel ? (
          <>
            <Label>{t('performance.profile.calibrationTitle')}</Label>
            <Muted>{t('performance.profile.levelChange', { proposed: b.proposedLevelLabel || '-', final: b.finalLevelLabel || '-' })}</Muted>
          </>
        ) : null}
      </>
    );
  } else if (item.type === 'action') {
    const a = item.action;
    title = a.title;
    body = (
      <Table>
        <tbody>
          <tr><td>{t('performance.action.actionType')}</td><td>{t(`performance.action.type.${a.actionType}`, { defaultValue: a.actionType })}</td></tr>
          <tr><td>{t('performance.action.status')}</td><td>{t(`performance.action.statusValue.${a.status}`, { defaultValue: a.status })}</td></tr>
          <tr><td>{t('performance.action.dueDate')}</td><td>{formatDate(a.dueDate, lang)}</td></tr>
        </tbody>
      </Table>
    );
  } else if (item.type === 'goal') {
    const g = item.goal;
    title = g.title;
    body = (
      <Table>
        <tbody>
          <tr><td>{t('performance.profile.goalStatus')}</td><td>{t(`performance.goal.status.${g.status}`, { defaultValue: g.status })}</td></tr>
          <tr><td>{t('performance.profile.weight')}</td><td>{g.weightPct != null ? `%${num(g.weightPct, lang)}` : '-'}</td></tr>
          <tr><td>{t('performance.profile.completion')}</td><td>{g.completionPct != null ? `%${num(g.completionPct, lang)}` : '-'}</td></tr>
          <tr><td>{t('performance.profile.krProgress')}</td><td>{g.avgKeyResultProgress != null ? `%${num(g.avgKeyResultProgress, lang)}` : '-'} ({g.keyResultCount})</td></tr>
          <tr><td>{t('performance.profile.lastUpdate')}</td><td>{g.lastCheckInAt ? formatDate(g.lastCheckInAt, lang) : '-'}</td></tr>
        </tbody>
      </Table>
    );
  } else {
    const e = item.entry;
    title = t(`performance.profile.timelineKind.${e.kind}`);
    body = (
      <>
        <Muted>{formatDate(e.at, lang)}{e.label ? ` · ${e.label}` : ''}{e.value != null ? ` · ${num(e.value, lang)}` : ''}</Muted>
        {e.code && e.kind === 'feedback' ? <Label>{t(`performance.profile.feedbackCategory.${e.code}`, { defaultValue: e.code })}</Label> : null}
        {e.text ? <Text>{e.text}</Text> : null}
        {e.kind === 'meeting' ? <Muted>{t('performance.profile.meetingsCountOnly')}</Muted> : null}
      </>
    );
  }
  return (
    <GenericFormDrawer open={open} onClose={onClose} title={title} schema={[]} submitLabel={t('common.close')} onSave={async () => true}>
      <div data-drawer={item.type}>{body}</div>
    </GenericFormDrawer>
  );
}
